/**
 * FLOW TEMPLATES COMPONENT
 * Előre definiált flow sablonok
 */

import React, { useState } from 'react';
import { 
  DocumentDuplicateIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  CogIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  ArrowRightIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { FlowTemplate, FlowNode, FlowEdge, FlowCategory } from '../../types/flowBuilder';

interface FlowTemplatesProps {
  onSelectTemplate: (template: FlowTemplate) => void;
  onClose?: () => void;
}

// Előre definiált sablonok
const FLOW_TEMPLATES: Omit<FlowTemplate, 'id' | 'organizationId' | 'createdAt' | 'updatedAt' | 'createdBy'>[] = [
  {
    name: 'Ügyfél Onboarding',
    description: 'Új ügyfél beillesztési folyamat automatizálása',
    category: 'customer_management',
    isActive: true,
    variables: [
      { name: 'customerName', type: 'string', scope: 'flow', value: '', description: 'Ügyfél neve' },
      { name: 'customerEmail', type: 'string', scope: 'flow', value: '', description: 'Ügyfél email címe' },
      { name: 'onboardingComplete', type: 'boolean', scope: 'flow', value: false, description: 'Onboarding befejezve' }
    ],
    triggers: [
      { id: 'trigger-1', type: 'webhook', config: { event: 'customer.created' }, isActive: true }
    ],
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        position: { x: 100, y: 50 },
        data: {
          label: 'Új Ügyfél Trigger',
          description: 'Új ügyfél regisztráció',
          config: { event: 'customer.created' },
          inputs: [],
          outputs: [{ name: 'customer', type: 'object', required: true, description: 'Ügyfél adatok' }]
        }
      },
      {
        id: 'crm-1',
        type: 'crm_action',
        position: { x: 100, y: 150 },
        data: {
          label: 'CRM Rekord Létrehozás',
          description: 'Ügyfél hozzáadása a CRM-hez',
          config: { action: 'create_customer' },
          inputs: [{ name: 'customer', type: 'object', required: true, description: 'Ügyfél adatok' }],
          outputs: [{ name: 'customerId', type: 'string', required: true, description: 'CRM ügyfél ID' }]
        }
      },
      {
        id: 'notification-1',
        type: 'notification',
        position: { x: 100, y: 250 },
        data: {
          label: 'Üdvözlő Email',
          description: 'Üdvözlő email küldése',
          config: { 
            type: 'email',
            template: 'welcome',
            to: '{{customer.email}}',
            subject: 'Üdvözöljük a {{organizationName}}-nál!'
          },
          inputs: [{ name: 'customer', type: 'object', required: true, description: 'Ügyfél adatok' }],
          outputs: [{ name: 'emailSent', type: 'boolean', required: true, description: 'Email elküldve' }]
        }
      },
      {
        id: 'variable-1',
        type: 'variable_set',
        position: { x: 100, y: 350 },
        data: {
          label: 'Onboarding Befejezés',
          description: 'Onboarding státusz beállítása',
          config: { variableName: 'onboardingComplete', variableValue: true },
          inputs: [],
          outputs: [{ name: 'result', type: 'boolean', required: true, description: 'Beállítás eredménye' }]
        }
      }
    ],
    edges: [
      {
        id: 'e1-2',
        source: 'trigger-1',
        target: 'crm-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e2-3',
        source: 'crm-1',
        target: 'notification-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e3-4',
        source: 'notification-1',
        target: 'variable-1',
        type: 'default',
        animated: false
      }
    ]
  },
  {
    name: 'Értékbecslési Folyamat',
    description: 'Automatikus értékbecslési workflow',
    category: 'project_management',
    isActive: true,
    variables: [
      { name: 'requestData', type: 'object', scope: 'flow', value: {}, description: 'Értékbecslési kérés adatai' },
      { name: 'estimateAmount', type: 'number', scope: 'flow', value: 0, description: 'Becsült összeg' },
      { name: 'approved', type: 'boolean', scope: 'flow', value: false, description: 'Jóváhagyva' }
    ],
    triggers: [
      { id: 'trigger-2', type: 'form_submission', config: { event: 'estimate.requested' }, isActive: true }
    ],
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        position: { x: 100, y: 50 },
        data: {
          label: 'Értékbecslési Kérés',
          description: 'Új értékbecslési kérés',
          config: { event: 'estimate.requested' },
          inputs: [],
          outputs: [{ name: 'request', type: 'object', required: true, description: 'Kérés adatai' }]
        }
      },
      {
        id: 'condition-1',
        type: 'condition',
        position: { x: 100, y: 150 },
        data: {
          label: 'Sürgős Kérés?',
          description: 'Ellenőrzi hogy sürgős-e',
          config: { 
            leftValue: '{{request.priority}}',
            operator: 'equals',
            rightValue: 'urgent'
          },
          inputs: [{ name: 'request', type: 'object', required: true, description: 'Kérés adatai' }],
          outputs: [
            { name: 'true', type: 'boolean', required: true, description: 'Sürgős' },
            { name: 'false', type: 'boolean', required: true, description: 'Normál' }
          ]
        }
      },
      {
        id: 'notification-urgent',
        type: 'notification',
        position: { x: 300, y: 100 },
        data: {
          label: 'Sürgős Értesítés',
          description: 'Azonnali értesítés küldése',
          config: { 
            type: 'email',
            to: 'admin@company.com',
            subject: 'SÜRGŐS: Értékbecslési kérés',
            priority: 'high'
          },
          inputs: [{ name: 'request', type: 'object', required: true, description: 'Kérés adatai' }],
          outputs: [{ name: 'sent', type: 'boolean', required: true, description: 'Elküldve' }]
        }
      },
      {
        id: 'crm-task',
        type: 'crm_action',
        position: { x: 100, y: 250 },
        data: {
          label: 'Feladat Létrehozás',
          description: 'Értékbecslési feladat létrehozása',
          config: { 
            action: 'create_task',
            title: 'Értékbecslés: {{request.title}}',
            assignTo: 'estimator',
            priority: '{{request.priority}}'
          },
          inputs: [{ name: 'request', type: 'object', required: true, description: 'Kérés adatai' }],
          outputs: [{ name: 'taskId', type: 'string', required: true, description: 'Feladat ID' }]
        }
      }
    ],
    edges: [
      {
        id: 'e1-2',
        source: 'trigger-1',
        target: 'condition-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e2-urgent',
        source: 'condition-1',
        target: 'notification-urgent',
        sourceHandle: 'true',
        type: 'default',
        animated: false
      },
      {
        id: 'e2-task',
        source: 'condition-1',
        target: 'crm-task',
        type: 'default',
        animated: false
      }
    ]
  },
  {
    name: 'Dokumentum Feldolgozás',
    description: 'Beérkező dokumentumok automatikus feldolgozása',
    category: 'document_processing',
    isActive: true,
    variables: [
      { name: 'document', type: 'object', scope: 'flow', value: {}, description: 'Dokumentum adatok' },
      { name: 'processed', type: 'boolean', scope: 'flow', value: false, description: 'Feldolgozva' }
    ],
    triggers: [
      { id: 'trigger-3', type: 'webhook', config: { event: 'document.uploaded' }, isActive: true }
    ],
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        position: { x: 100, y: 50 },
        data: {
          label: 'Dokumentum Feltöltés',
          description: 'Új dokumentum érkezett',
          config: { event: 'document.uploaded' },
          inputs: [],
          outputs: [{ name: 'document', type: 'object', required: true, description: 'Dokumentum adatok' }]
        }
      },
      {
        id: 'text-processor-1',
        type: 'text_processor',
        position: { x: 100, y: 150 },
        data: {
          label: 'Szöveg Kinyerés',
          description: 'Szöveg kinyerése a dokumentumból',
          config: { operation: 'extract_text' },
          inputs: [{ name: 'document', type: 'object', required: true, description: 'Dokumentum' }],
          outputs: [{ name: 'text', type: 'string', required: true, description: 'Kinyert szöveg' }]
        }
      },
      {
        id: 'json-processor-1',
        type: 'json_processor',
        position: { x: 100, y: 250 },
        data: {
          label: 'Metaadatok Kinyerés',
          description: 'Dokumentum metaadatok feldolgozása',
          config: { operation: 'extract', path: 'metadata' },
          inputs: [{ name: 'document', type: 'object', required: true, description: 'Dokumentum' }],
          outputs: [{ name: 'metadata', type: 'object', required: true, description: 'Metaadatok' }]
        }
      }
    ],
    edges: [
      {
        id: 'e1-2',
        source: 'trigger-1',
        target: 'text-processor-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e1-3',
        source: 'trigger-1',
        target: 'json-processor-1',
        type: 'default',
        animated: false
      }
    ]
  },
  {
    name: 'Fejlett Adatfeldolgozás',
    description: 'Komplex adatfeldolgozási workflow loop-okkal és feltételekkel',
    category: 'data_processing',
    isActive: true,
    variables: [
      { name: 'inputData', type: 'array', scope: 'flow', value: [], description: 'Feldolgozandó adatok' },
      { name: 'processedCount', type: 'number', scope: 'flow', value: 0, description: 'Feldolgozott elemek száma' },
      { name: 'errorCount', type: 'number', scope: 'flow', value: 0, description: 'Hibás elemek száma' }
    ],
    triggers: [
      { id: 'trigger-4', type: 'manual', config: { name: 'Adatfeldolgozás indítása' }, isActive: true }
    ],
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        position: { x: 100, y: 50 },
        data: {
          label: 'Adatfeldolgozás Indítás',
          description: 'Manuális indítás adatfeldolgozáshoz',
          config: { name: 'Adatfeldolgozás indítása' },
          inputs: [],
          outputs: [{ name: 'data', type: 'array', required: true, description: 'Bemeneti adatok' }]
        }
      },
      {
        id: 'variable-1',
        type: 'variable_set',
        position: { x: 300, y: 50 },
        data: {
          label: 'Számláló Inicializálás',
          description: 'Feldolgozott elemek számának nullázása',
          config: { variableName: 'processedCount', variableValue: 0 },
          inputs: [{ name: 'trigger', type: 'object', required: true, description: 'Trigger' }],
          outputs: [{ name: 'value', type: 'number', required: true, description: 'Beállított érték' }]
        }
      },
      {
        id: 'loop-1',
        type: 'loop',
        position: { x: 500, y: 50 },
        data: {
          label: 'Adatok Feldolgozása',
          description: 'Végigmegy az összes elemen',
          config: { maxIterations: 100, loopVariable: 'currentItem', arrayVariable: 'inputData' },
          inputs: [{ name: 'data', type: 'array', required: true, description: 'Feldolgozandó adatok' }],
          outputs: [
            { name: 'loop-body', type: 'object', required: true, description: 'Loop törzs' },
            { name: 'complete', type: 'object', required: true, description: 'Loop befejezve' }
          ]
        }
      },
      {
        id: 'condition-1',
        type: 'condition',
        position: { x: 500, y: 200 },
        data: {
          label: 'Érvényesség Ellenőrzés',
          description: 'Ellenőrzi az elem érvényességét',
          config: { 
            condition: '{{currentItem}} != null && {{currentItem}} != ""',
            leftValue: '{{currentItem}}',
            operator: 'not_equals',
            rightValue: null
          },
          inputs: [{ name: 'item', type: 'object', required: true, description: 'Vizsgálandó elem' }],
          outputs: [
            { name: 'true', type: 'object', required: true, description: 'Érvényes elem' },
            { name: 'false', type: 'object', required: true, description: 'Érvénytelen elem' }
          ]
        }
      },
      {
        id: 'text-processor-1',
        type: 'text_processor',
        position: { x: 700, y: 150 },
        data: {
          label: 'Szöveg Normalizálás',
          description: 'Szöveg tisztítása és formázása',
          config: { operation: 'trim', inputText: '{{currentItem}}', resultVariable: 'cleanedItem' },
          inputs: [{ name: 'text', type: 'string', required: true, description: 'Nyers szöveg' }],
          outputs: [{ name: 'result', type: 'string', required: true, description: 'Tisztított szöveg' }]
        }
      },
      {
        id: 'math-1',
        type: 'math_operation',
        position: { x: 900, y: 150 },
        data: {
          label: 'Számláló Növelés',
          description: 'Feldolgozott elemek számának növelése',
          config: { 
            operation: 'add', 
            leftOperand: '{{processedCount}}', 
            rightOperand: 1,
            resultVariable: 'processedCount'
          },
          inputs: [
            { name: 'a', type: 'number', required: true, description: 'Jelenlegi szám' },
            { name: 'b', type: 'number', required: true, description: 'Hozzáadandó érték' }
          ],
          outputs: [{ name: 'result', type: 'number', required: true, description: 'Eredmény' }]
        }
      },
      {
        id: 'debug-1',
        type: 'debug_logger',
        position: { x: 700, y: 250 },
        data: {
          label: 'Hiba Naplózás',
          description: 'Érvénytelen elemek naplózása',
          config: { 
            logLevel: 'warn',
            message: 'Érvénytelen elem találva: {{currentItem}}',
            logData: '{{currentItem}}'
          },
          inputs: [{ name: 'data', type: 'object', required: true, description: 'Naplózandó adat' }],
          outputs: [{ name: 'data', type: 'object', required: true, description: 'Továbbított adat' }]
        }
      },
      {
        id: 'merge-1',
        type: 'merge',
        position: { x: 1100, y: 200 },
        data: {
          label: 'Eredmények Egyesítése',
          description: 'Sikeres és hibás feldolgozások egyesítése',
          config: { waitForAll: false, inputCount: 2 },
          inputs: [
            { name: 'input1', type: 'object', required: false, description: 'Sikeres feldolgozás' },
            { name: 'input2', type: 'object', required: false, description: 'Hibás feldolgozás' }
          ],
          outputs: [{ name: 'output', type: 'object', required: true, description: 'Egyesített eredmény' }]
        }
      },
      {
        id: 'wait-1',
        type: 'wait',
        position: { x: 1300, y: 200 },
        data: {
          label: 'Rövid Szünet',
          description: 'Rövid várakozás a következő iteráció előtt',
          config: { duration: 100, unit: 'milliseconds' },
          inputs: [{ name: 'input', type: 'object', required: true, description: 'Bemenet' }],
          outputs: [{ name: 'output', type: 'object', required: true, description: 'Kimenet' }]
        }
      },
      {
        id: 'notification-1',
        type: 'notification',
        position: { x: 700, y: 50 },
        data: {
          label: 'Befejezés Értesítés',
          description: 'Értesítés a feldolgozás befejezéséről',
          config: { 
            type: 'email',
            recipient: 'admin@youcrm.hu',
            subject: 'Adatfeldolgozás befejezve',
            message: 'Összesen {{processedCount}} elem feldolgozva.'
          },
          inputs: [
            { name: 'recipient', type: 'string', required: true, description: 'Címzett' },
            { name: 'subject', type: 'string', required: true, description: 'Tárgy' },
            { name: 'body', type: 'string', required: true, description: 'Üzenet' }
          ],
          outputs: [{ name: 'result', type: 'object', required: true, description: 'Küldés eredménye' }]
        }
      }
    ],
    edges: [
      {
        id: 'e1-2',
        source: 'trigger-1',
        target: 'variable-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e2-3',
        source: 'variable-1',
        target: 'loop-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e3-4',
        source: 'loop-1',
        target: 'condition-1',
        sourceHandle: 'loop-body',
        type: 'default',
        animated: false
      },
      {
        id: 'e4-5',
        source: 'condition-1',
        target: 'text-processor-1',
        sourceHandle: 'true',
        type: 'default',
        animated: false
      },
      {
        id: 'e4-6',
        source: 'condition-1',
        target: 'debug-1',
        sourceHandle: 'false',
        type: 'default',
        animated: false
      },
      {
        id: 'e5-7',
        source: 'text-processor-1',
        target: 'math-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e7-8',
        source: 'math-1',
        target: 'merge-1',
        targetHandle: 'input1',
        type: 'default',
        animated: false
      },
      {
        id: 'e6-8',
        source: 'debug-1',
        target: 'merge-1',
        targetHandle: 'input2',
        type: 'default',
        animated: false
      },
      {
        id: 'e8-9',
        source: 'merge-1',
        target: 'wait-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e9-3',
        source: 'wait-1',
        target: 'loop-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e3-10',
        source: 'loop-1',
        target: 'notification-1',
        sourceHandle: 'complete',
        type: 'default',
        animated: false
      }
    ]
  },
  {
    name: 'Hiányzó Bizonylatok Emlékeztető',
    description: 'Heti automatikus emlékeztető küldése ügyfeleknek hiányzó bizonylatokról',
    category: 'communication',
    isActive: true,
    variables: [
      { name: 'customers', type: 'array', scope: 'flow', value: [], description: 'Ügyfelek hiányzó bizonylatokkal' },
      { name: 'emailsSent', type: 'number', scope: 'flow', value: 0, description: 'Elküldött emailek száma' }
    ],
    triggers: [
      { id: 'trigger-5', type: 'schedule', config: { schedule: 'weekly', day: 'monday', time: '09:00' }, isActive: true }
    ],
    nodes: [
      {
        id: 'trigger-1',
        type: 'schedule_trigger',
        position: { x: 100, y: 50 },
        data: {
          label: 'Heti Trigger',
          description: 'Minden hétfő 9:00-kor',
          config: { schedule: 'weekly', day: 'monday', time: '09:00' },
          inputs: [],
          outputs: [{ name: 'timestamp', type: 'string', required: true, description: 'Trigger időpontja' }]
        }
      },
      {
        id: 'missing-items-1',
        type: 'missing_items_query',
        position: { x: 100, y: 150 },
        data: {
          label: 'Hiányzó Tételek Lekérdezése',
          description: 'Hiányzó tételek és érintett ügyfelek',
          config: { 
            statusFilter: 'pending',
            includeCompleted: false,
            sortBy: 'createdAt',
            groupByCustomer: true
          },
          inputs: [],
          outputs: [
            { name: 'missingItems', type: 'array', required: true, description: 'Hiányzó tételek' },
            { name: 'customers', type: 'array', required: true, description: 'Érintett ügyfelek' }
          ]
        }
      },
      {
        id: 'loop-1',
        type: 'loop',
        position: { x: 100, y: 250 },
        data: {
          label: 'Ügyfelek Feldolgozása',
          description: 'Végigmegy minden ügyfélen',
          config: { 
            maxIterations: 100, 
            loopVariable: 'customer', 
            arrayVariable: 'customers' 
          },
          inputs: [{ name: 'customers', type: 'array', required: true, description: 'Ügyfelek' }],
          outputs: [
            { name: 'loop-body', type: 'object', required: true, description: 'Loop törzs' },
            { name: 'complete', type: 'object', required: true, description: 'Loop befejezve' }
          ]
        }
      },
      {
        id: 'smtp-email-1',
        type: 'smtp_email',
        position: { x: 300, y: 250 },
        data: {
          label: 'Email Küldés',
          description: 'Konfiguráld az email template-et és címzettet',
          config: { 
            recipientMode: 'single',
            recipient: '{{customer.email}}'
          },
          inputs: [{ name: 'customer', type: 'object', required: true, description: 'Ügyfél adatok' }],
          outputs: [{ name: 'emailSent', type: 'boolean', required: true, description: 'Email elküldve' }]
        }
      },
      {
        id: 'wait-1',
        type: 'wait',
        position: { x: 500, y: 250 },
        data: {
          label: 'Várakozás',
          description: '2 másodperc késleltetés',
          config: { duration: 2, unit: 'seconds' },
          inputs: [{ name: 'input', type: 'object', required: true, description: 'Bemenet' }],
          outputs: [{ name: 'output', type: 'object', required: true, description: 'Kimenet' }]
        }
      },
      {
        id: 'notification-success',
        type: 'notification',
        position: { x: 500, y: 100 },
        data: {
          label: 'Összesítő Email',
          description: 'Admin értesítés az elküldött emailekről',
          config: { 
            type: 'email',
            to: 'admin@perfectgroup.hu',
            subject: 'Heti emlékeztető összesítő',
            message: '{{customers.length}} ügyfélnek elküldve emlékeztető hiányzó bizonylatokról.'
          },
          inputs: [{ name: 'customers', type: 'array', required: true, description: 'Ügyfelek' }],
          outputs: [{ name: 'sent', type: 'boolean', required: true, description: 'Elküldve' }]
        }
      }
    ],
    edges: [
      {
        id: 'e1-2',
        source: 'trigger-1',
        target: 'missing-items-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e2-loop',
        source: 'missing-items-1',
        target: 'loop-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e-loop-email',
        source: 'loop-1',
        target: 'smtp-email-1',
        sourceHandle: 'loop-body',
        type: 'default',
        animated: false
      },
      {
        id: 'e-email-wait',
        source: 'smtp-email-1',
        target: 'wait-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e-wait-loop',
        source: 'wait-1',
        target: 'loop-1',
        type: 'default',
        animated: false
      },
      {
        id: 'e-loop-notification',
        source: 'loop-1',
        target: 'notification-success',
        sourceHandle: 'complete',
        type: 'default',
        animated: false
      }
    ]
  }
];

const FlowTemplates: React.FC<FlowTemplatesProps> = ({ 
  onSelectTemplate, 
  onClose 
}) => {
  const [selectedCategory, setSelectedCategory] = useState<FlowCategory | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'all', name: 'Összes', icon: CogIcon },
    { id: 'customer_management', name: 'Ügyfélkezelés', icon: UserGroupIcon },
    { id: 'project_management', name: 'Projektmenedzsment', icon: RocketLaunchIcon },
    { id: 'document_processing', name: 'Dokumentumkezelés', icon: DocumentTextIcon },
    { id: 'data_processing', name: 'Adatfeldolgozás', icon: SparklesIcon },
    { id: 'communication', name: 'Kommunikáció', icon: EnvelopeIcon },
  ] as const;

  const filteredTemplates = FLOW_TEMPLATES.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const handleSelectTemplate = (template: typeof FLOW_TEMPLATES[0]) => {
    const fullTemplate: FlowTemplate = {
      ...template,
      id: '',
      organizationId: '',
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: ''
    };
    onSelectTemplate(fullTemplate);
  };

  const getCategoryIcon = (category: FlowCategory) => {
    const cat = categories.find(c => c.id === category);
    return cat?.icon || CogIcon;
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-x-hidden">
      {/* Modern Header with Glassmorphism */}
      <div className="relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-transparent to-primary-500/10" />
        
        <div className="relative backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-b border-white/20 dark:border-gray-700/50 shadow-lg">
          <div className="p-4">
            {/* Subtitle */}
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
                <SparklesIcon className="h-4 w-4 text-primary-500" />
                <span>Válassz egy sablont a gyors kezdéshez</span>
              </p>
            </div>

            {/* Modern Search */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Keresés sablonokban..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/70 dark:bg-gray-700/70 backdrop-blur-sm border border-white/20 dark:border-gray-600/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 shadow-sm transition-all duration-200"
              />
            </div>

            {/* Modern Category Pills */}
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => {
                const Icon = category.icon;
                const isSelected = selectedCategory === category.id;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id as FlowCategory | 'all')}
                    className={`group flex items-center px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isSelected
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25 scale-105'
                        : 'bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm border border-white/20 dark:border-gray-600/20 text-gray-700 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-gray-700/80 hover:scale-105 hover:shadow-md'
                    }`}
                  >
                    <Icon className={`h-4 w-4 mr-2 transition-transform duration-200 ${isSelected ? 'scale-110' : 'group-hover:scale-110'}`} />
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Modern Templates Grid */}
      <div className="flex-1 overflow-y-auto p-4 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-primary-300/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-400/70">
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-16">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 via-transparent to-primary-500/10 rounded-full blur-3xl" />
              <div className="relative p-8 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl border border-white/20 dark:border-gray-700/20 shadow-xl max-w-md mx-auto">
                <DocumentDuplicateIcon className="h-16 w-16 text-primary-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Nincs találat
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Nincs sablon a keresési feltételeknek megfelelően
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTemplates.map((template, index) => {
              const CategoryIcon = getCategoryIcon(template.category);
              const categoryName = categories.find(c => c.id === template.category)?.name;
              
              return (
                <div
                  key={index}
                  className="group relative cursor-pointer"
                  onClick={() => handleSelectTemplate(template)}
                >
                  {/* Card Background with Gradient Border */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 via-transparent to-primary-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl border border-white/20 dark:border-gray-700/20 p-4 shadow-lg hover:shadow-2xl hover:shadow-primary-500/10 transition-all duration-300 group-hover:scale-[1.02] group-hover:border-primary-500/30">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl blur opacity-50 group-hover:opacity-75 transition-opacity duration-300" />
                          <div className="relative p-3 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg">
                            <CategoryIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200">
                            {template.name}
                          </h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <TagIcon className="h-3 w-3 text-gray-400" />
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              {categoryName}
                            </span>
                          </div>
                        </div>
                      </div>
                      <ArrowRightIcon className="h-5 w-5 text-gray-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all duration-200" />
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                      {template.description}
                    </p>

                    {/* Stats */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-primary-500 rounded-full" />
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {template.nodes.length} node
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full" />
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {template.variables.length} változó
                          </span>
                        </div>
                      </div>
                      
                      <div className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-semibold rounded-full">
                        Sablon
                      </div>
                    </div>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-primary-600/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowTemplates;
